import type { TooltipContent } from "../render/RenderContext.js";
import { Container, type ContainerOptions } from "../core/Container.js";
import type { Control } from "../core/Control.js";
import type { MouseEventArgs } from "../core/events.js";
import type { Point, Rect, Size } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";

export type StatusPanelStyle = "sunken" | "flat" | "none";

export interface StatusPanelSpec {
  text?: string;
  /**
   * Fixed width in px, or "spring" to share whatever is left over. Several
   * springs divide it equally, which is how a status bar keeps one wide
   * message area beside a few fixed readouts.
   */
  width?: number | "spring";
  align?: "left" | "center" | "right";
  style?: StatusPanelStyle;
  tooltip?: string;
  /**
   * A control to sit in this panel — a ProgressBar is the usual one. The
   * status bar positions and sizes it; add it to `controls` as well.
   */
  content?: Control | null;
  onClick?: (panel: StatusPanelSpec) => void;
}

export interface StatusBarOptions extends ContainerOptions {
  panels?: StatusPanelSpec[];
  /** Draw the resize grip at the right-hand end. */
  showGrip?: boolean;
}

const PANEL_GAP = 2;
const TEXT_PADDING = 7;
const GRIP_SIZE = 14;
/** Room above and below a panel's text. Leaves the default bar 24px at the default font. */
const TEXT_HEIGHT_PADDING = 4;

/**
 * The strip along the bottom of a window: a row of panels, some fixed width
 * and some sharing the slack, with an optional resize grip.
 *
 * A Container as well as a panel model, because the common status bar holds a
 * progress bar as well as text. Give a panel `content` and the bar positions
 * that control inside it.
 */
export class StatusBar extends Container {
  #panels: StatusPanelSpec[];
  showGrip: boolean;

  constructor(options: StatusBarOptions = {}) {
    super({ width: 400, height: 24, tabStop: false, cursor: "default", ...options });
    this.#panels = options.panels ?? [];
    this.showGrip = options.showGrip ?? false;
    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.performLayout();
  }

  get panels(): StatusPanelSpec[] {
    return this.#panels;
  }
  set panels(v: StatusPanelSpec[]) {
    this.#panels = v;
    this.performLayout();
    this.invalidate();
  }

  /** Convenience for the common "one message area" case. */
  get text(): string {
    return this.#panels[0]?.text ?? "";
  }
  set text(v: string) {
    if (this.#panels.length === 0) this.#panels.push({ width: "spring" });
    this.#panels[0]!.text = v;
    this.invalidate();
  }

  setPanelText(index: number, text: string): void {
    const panel = this.#panels[index];
    if (!panel || panel.text === text) return;
    panel.text = text;
    this.invalidate();
  }

  // --- geometry ---------------------------------------------------------

  private get gripWidth(): number {
    return this.showGrip ? GRIP_SIZE : 0;
  }

  /** Panel rectangles, left to right, in local coordinates. */
  panelRects(): Rect[] {
    const top = 2;
    const height = Math.max(0, this.height - 4);
    const available = Math.max(0, this.width - 4 - this.gripWidth);

    let fixed = 0;
    let springs = 0;
    for (const p of this.#panels) {
      if (p.width === "spring" || p.width === undefined) springs++;
      else fixed += p.width;
    }
    const gaps = PANEL_GAP * Math.max(0, this.#panels.length - 1);
    const slack = Math.max(0, available - fixed - gaps);
    const perSpring = springs > 0 ? slack / springs : 0;

    const rects: Rect[] = [];
    let x = 2;
    for (const p of this.#panels) {
      const w = p.width === "spring" || p.width === undefined ? perSpring : p.width;
      rects.push({ x, y: top, width: Math.max(0, w), height });
      x += w + PANEL_GAP;
    }
    return rects;
  }

  indexAt(p: Point): number {
    const rects = this.panelRects();
    for (let i = 0; i < rects.length; i++) {
      const r = rects[i]!;
      if (p.x >= r.x && p.x < r.x + r.width && p.y >= r.y && p.y < r.y + r.height) return i;
    }
    return -1;
  }

  override getTooltip(p: Point): TooltipContent | null {
    const i = this.indexAt(p);
    return this.#panels[i]?.tooltip ?? this.tooltip;
  }

  /** Position any hosted controls into their panels. */
  protected override performLayout(): void {
    if (!this.#panels) return;
    const rects = this.panelRects();
    this.#panels.forEach((panel, i) => {
      const content = panel.content;
      const r = rects[i];
      if (!content || !r) return;
      // Leave a little breathing room inside the panel's own chrome.
      const inset = panel.style === "none" ? 0 : 2;
      content.setBounds(
        r.x + inset,
        r.y + inset,
        Math.max(0, r.width - inset * 2),
        Math.max(0, r.height - inset * 2),
      );
    });
  }

  #onPointerDown(e: MouseEventArgs): void {
    const i = this.indexAt({ x: e.x, y: e.y });
    const panel = this.#panels[i];
    if (!panel?.onClick) return;
    e.handled = true;
    panel.onClick(panel);
  }

  // --- painting ---------------------------------------------------------

  protected override hitTestSelf(): boolean {
    return true;
  }


  /** As wide as it is given, and as tall as one line of text plus its padding. */
  override preferredSize(available: Size): Size {
    const desktop = this.desktop;
    if (!desktop) return this.size;
    const line = desktop.rc.lineHeight(desktop.theme.metrics.font);
    return { width: available.width, height: Math.ceil(line + TEXT_HEIGHT_PADDING * 2) };
  }

  protected override onEnvironmentChanged(): void {
    this.invalidateMeasure();
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    rc.fillRect(this.localRect, p.windowBg);
    rc.hLine(0, 0, this.width, p.gridLine);

    const font = theme.metrics.font;
    const rects = this.panelRects();
    this.#panels.forEach((panel, i) => {
      const r = rects[i];
      if (!r || r.width <= 0) return;

      const style = panel.style ?? "sunken";
      if (style === "sunken") theme.drawPanel(rc, r, true);
      else if (style === "flat") rc.fillRect(r, p.controlBg, theme.metrics.controlCornerRadius);

      // A panel hosting a control draws no text of its own.
      if (panel.content || !panel.text) return;
      const inner: Rect = {
        x: r.x + TEXT_PADDING,
        y: r.y,
        width: Math.max(0, r.width - TEXT_PADDING * 2),
        height: r.height,
      };
      const align = panel.align ?? "left";
      if (align === "left") {
        rc.drawTextInRect(panel.text, inner, font, p.text);
      } else {
        const shown = rc.ellipsize(panel.text, font, inner.width);
        const w = rc.measureText(shown, font).width;
        const x = align === "center" ? inner.x + (inner.width - w) / 2 : inner.x + inner.width - w;
        rc.drawText(shown, x, r.y + r.height / 2, font, p.text, {
          baseline: "middle",
        });
      }
    });

    if (this.showGrip) this.#drawGrip(rc, theme);
  }

  /** The diagonal dot pattern Windows puts in the bottom-right corner. */
  #drawGrip(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    const x1 = this.width - 3;
    const y1 = this.height - 3;
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col <= row; col++) {
        const x = x1 - col * 4;
        const y = y1 - (row - col) * 4;
        rc.fillRect({ x, y, width: 2, height: 2 }, p.bevelShadow);
        rc.fillRect({ x: x - 1, y: y - 1, width: 1, height: 1 }, p.bevelHighlight);
      }
    }
  }
}
